import { FC, PointerEvent as ReactPointerEvent, useRef, useState } from "react";

import { useRtl } from "@ryde/redux";
import { useSystemParams } from "@ryde/shared";

import {
  INVESTIGATION_PANEL_WIDTH,
  INVESTIGATION_PANEL_WIDTH_WITH_AI,
} from "./investigation.constants";
import { InvestigationPanel } from "./investigation.panel";
import { InvestigationTopBar } from "./investigation.top-bar";
import { panelWidthForPointer } from "./investigation.utils";
import {
  MapAreaStyled,
  MapColumnStyled,
  PanelColumnStyled,
  ResizerStyled,
  WorkspaceStyled,
} from "./investigation.workspace.styled";
import { InvestigationMap } from "./map/investigation.map";
import { InvestigationTransportBar } from "./transport/investigation.transport-bar";

/**
 * Full-body investigation workspace: a dedicated map (with a layers top bar and the player docked below)
 * on the left, a draggable resizer, and the info panel on the right. It fills its host — the full-screen
 * investigation overlay — so the docked transport bar can never be clipped.
 */
export const InvestigationWorkspace: FC = () => {
  const { enableAIFeatures } = useSystemParams();
  const { isRtl } = useRtl();
  // Read once, on mount: the overlay unmounts on close, so each investigation opens at the right width
  // and a user who has since dragged the split keeps their size for the rest of the session.
  const [panelWidth, setPanelWidth] = useState(
    enableAIFeatures
      ? INVESTIGATION_PANEL_WIDTH_WITH_AI
      : INVESTIGATION_PANEL_WIDTH,
  );
  const workspaceRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (draggingRef.current) {
      const rect = workspaceRef.current?.getBoundingClientRect();

      if (rect) {
        setPanelWidth(
          panelWidthForPointer({
            workspaceLeft: rect.left,
            workspaceRight: rect.right,
            clientX: event.clientX,
            isRtl,
          }),
        );
      }
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    draggingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <WorkspaceStyled ref={workspaceRef}>
      <MapColumnStyled>
        <InvestigationTopBar />
        <MapAreaStyled>
          <InvestigationMap />
        </MapAreaStyled>
        <InvestigationTransportBar />
      </MapColumnStyled>

      <ResizerStyled
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      <PanelColumnStyled style={{ width: panelWidth }}>
        <InvestigationPanel />
      </PanelColumnStyled>
    </WorkspaceStyled>
  );
};
